// src/components/MenuBar.tsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, User, Camera, Settings, Mail, Menu, X, LogIn } from 'lucide-react';
import './MenuBar.css';

interface MenuBarProps {
  onHomeClick?: () => void;
  onFeaturesClick?: () => void;
  onUsageClick?: () => void;
  onContactClick?: () => void;
}

const MenuBar: React.FC<MenuBarProps> = ({ onHomeClick, onFeaturesClick, onUsageClick, onContactClick }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path ? 'active' : '';
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const handleLoginClick = () => {
    navigate('/auth/login');
    closeMenu();
  };

  // ページ変更時にメニューを閉じる
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // ESCキーでメニューを閉じる
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // スクロール防止
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      {/* ヘッダーバー */}
      <div className="header-bar">
        
        {/* ログインボタン */}
        <button 
          className="login-button"
          onClick={handleLoginClick}
          aria-label="ログインページへ"
        >
          <LogIn size={20} />
          <span>ログイン</span>
        </button>
        
        {/* ハンバーガーメニューボタン */}
        <button 
          className="hamburger-button"
          onClick={toggleMenu}
          aria-label="メニューを開く"
          aria-expanded={isOpen}
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

      </div>

      {/* モバイルメニューオーバーレイ */}
      {isOpen && (
        <div className="menu-overlay" onClick={closeMenu}>
          <div className="mobile-menu" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <h3>メニュー</h3>
              <button 
                className="close-button"
                onClick={closeMenu}
                aria-label="メニューを閉じる"
              >
                <X size={24} />
              </button>
            </div>
            <ul className="mobile-menu-list">
              <li>
                <button 
                  className="menu-scroll-button"
                  onClick={() => {
                    closeMenu();
                    onHomeClick?.();
                  }}
                >
                  <Home size={20} />
                  ホーム
                </button>
              </li>
              <li>
                <button 
                  className="menu-scroll-button"
                  onClick={() => {
                    closeMenu();
                    onFeaturesClick?.();
                  }}
                >
                  <User size={20} />
                  4つの主要機能
                </button>
              </li>
              <li>
                <button 
                  className="menu-scroll-button"
                  onClick={() => {
                    closeMenu();
                    onUsageClick?.();
                  }}
                >
                  <Camera size={20} />
                  Photo Memorialの使用方法
                </button>
              </li>
              <li>
                <button 
                  className="menu-scroll-button"
                  onClick={() => {
                    closeMenu();
                    onContactClick?.();
                  }}
                >
                  <Mail size={20} />
                  お問い合わせをする
                </button>
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default MenuBar;
